import React, {useEffect, useRef, useState} from 'react'
import { Link } from 'react-router-dom';
import './Campaign.css';

import Loader from '../../../components/Loader/Loader';
import { getAccountAdvertisement, updateAccountAdvertisement, uploadAccountAdvertisementPhoto, uploadAccountAdvertisementVideo } from '../../../../services/accountService';
import { DEFAULT_PROFILE_IMAGE } from '../../../../util/ModelPhotos';

const italianCities = ['Rome', 'Milan', 'Florence', 'Naples', 'Venice', 'Turin', 'Bologna', 'Palermo', 'Genoa', 'Verona'];
const durationOptions = ['30 Minutes', '1 Hour', '2 Hours', '3 Hours', '4 Hours', '6 Hours', '8 Hours', '12 Hours', '24 Hours', '48 Hours', '72 Hours'];
const heightOptions = ['N/A', ...Array.from({length: 54}, (_, index) => `${140 + index}cm`)];
const weightOptions = ['N/A', ...Array.from({length: 61}, (_, index) => `${40 + index}kg`)];
const contactKeys = ['whatsapp', 'telegram', 'phone', 'email'];
const contactLabel = (key) => key.replace(/[-_]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
const parameterDefaults = {height: '', weight: '', body: '', hair: '', eyes: '', breast: ''};
const contactDefaults = {whatsapp: '', telegram: '', phone: '', email: ''};
const allowedPhotoTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif']);
const maxPhotoSize = 10 * 1024 * 1024;
const maxPhotos = 5;
const allowedVideoTypes = new Set(['video/mp4', 'video/webm', 'video/quicktime']);
const maxVideoSize = 100 * 1024 * 1024;
const maxVideos = 2;
const modelStatusOptions = [
    {value: 'active', label: 'Active'},
    {value: 'inactive', label: 'Inactive'},
    {value: 'break', label: 'Break'}
];

const normalizeModelStatus = (value) => {
    const normalized = String(value || '').toLowerCase().replace(/[\s_-]+/g, '');
    if (normalized === 'active') return 'active';
    if (normalized === 'break' || normalized === 'paused') return 'break';
    return 'inactive';
};

const valueOrEmpty = (value) => value === null || value === undefined ? '' : value;
const copyRates = (rates) => Array.isArray(rates) ? rates.map((rate) => ({...rate})) : [];
const slugifyPathSegment = (value) => {
    const segment = typeof value === 'object' ? value?.slug || value?.name : value;
    return String(segment || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
};
const modelPublicPath = (model) => `/models/${encodeURIComponent(slugifyPathSegment(model?.citySlug || model?.city || 'Rome'))}/${encodeURIComponent(slugifyPathSegment(model?.slug || model?.name))}`;

const applyAdvertisement = (advertisement, setters) => {
    setters.setModel(advertisement)
    setters.setPhotos(Array.isArray(advertisement?.photos) ? [...advertisement.photos] : [])
    setters.setVideos(Array.isArray(advertisement?.videos) ? [...advertisement.videos] : [])
    setters.setName(advertisement?.name || '')
    setters.setDescription(advertisement?.description || '')
    setters.setParams({...parameterDefaults, ...(advertisement?.parameters || {})})
    setters.setCityRates(copyRates(advertisement?.cityRates))
    setters.setTravelRates(copyRates(advertisement?.travelRates))
    setters.setLocationType(advertisement?.locationType || 'single')
    setters.setSingleCity(advertisement?.singleCity || advertisement?.city || 'Rome')
    setters.setTourSchedule(Array.isArray(advertisement?.tourSchedule) ? advertisement.tourSchedule.map((stop) => ({...stop})) : [])
    setters.setContacts({...contactDefaults, ...(advertisement?.contacts || {})})
    setters.setStatus(normalizeModelStatus(advertisement?.status));
};

const Advertisement = ({modelId}) => {
    const [model, setModel] = useState(null)
    const [photos, setPhotos] = useState([])
    const [videos, setVideos] = useState([])
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [params, setParams] = useState(parameterDefaults)
    const [cityRates, setCityRates] = useState([])
    const [travelRates, setTravelRates] = useState([])
    const [locationType, setLocationType] = useState('single')
    const [singleCity, setSingleCity] = useState('Rome')
    const [tourSchedule, setTourSchedule] = useState([])
    const [contacts, setContacts] = useState(contactDefaults)
    const [status, setStatus] = useState('inactive')

    const [showUpload, setShowUpload] = useState(false)
    const [showVideoUpload, setShowVideoUpload] = useState(false)
    const [photoFile, setPhotoFile] = useState(null)
    const [videoFile, setVideoFile] = useState(null)
    const [uploadingPhoto, setUploadingPhoto] = useState(false)
    const [uploadingVideo, setUploadingVideo] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(null)
    const [lightbox, setLightbox] = useState(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [dockStopped, setDockStopped] = useState(false)
    const photoPointerRef = useRef(null)
    const [error, setError] = useState('')
    const [saveMessage, setSaveMessage] = useState('')
    const [retryToken, setRetryToken] = useState(0)

    useEffect(() => {
        let mounted = true
        setLoading(true)
        setError('')
        getAccountAdvertisement(modelId)
            .then((advertisement) => {
                if (!mounted) return
                applyAdvertisement(advertisement, {setModel, setPhotos, setVideos, setName, setDescription, setParams, setCityRates, setTravelRates, setLocationType, setSingleCity, setTourSchedule, setContacts, setStatus})
            })
            .catch((loadError) => {
                if (mounted) setError(loadError.message || 'We could not load your advertisement.')
            })
            .finally(() => {
                if (mounted) setLoading(false)
            })
        return () => { mounted = false }
    }, [modelId, retryToken])

    useEffect(() => {
        const updateDockPosition = () => {
            const contactSection = document.querySelector('.section-contact')
            if (!contactSection) return
            setDockStopped(contactSection.getBoundingClientRect().top <= window.innerHeight)
        }

        window.addEventListener('scroll', updateDockPosition, {passive: true})
        window.addEventListener('resize', updateDockPosition)
        return () => {
            window.removeEventListener('scroll', updateDockPosition)
            window.removeEventListener('resize', updateDockPosition)
        }
    }, [])

    const persist = async (changes, onSuccess) => {
        if (!model || saving) return false
        setSaving(true)
        setError('')
        setSaveMessage('')
        try {
            const saved = await updateAccountAdvertisement(model.uuid, changes)
            const nextModel = {...model, ...changes, ...(saved || {})}
            setModel(nextModel)
            if (onSuccess) onSuccess(nextModel)
            setSaveMessage('Advertisement saved.')
            return true
        } catch (saveError) {
            setError(saveError.message || 'We could not save your advertisement.')
            return false
        } finally {
            setSaving(false)
        }
    }

    const saveAll = async () => {
        const profileChanges = {
            name: name.trim(),
            description: description.trim(),
            parameters: params,
            contacts,
            locationType,
            city: singleCity,
            singleCity,
            tourSchedule,
            cityRates,
            travelRates,
            status
        }
        await persist(profileChanges)
    }

    const handleParamChange = (key, value) => setParams((current) => ({...current, [key]: value}));
    const paramSelect = (key) => key === 'height' ? heightOptions : key === 'weight' ? weightOptions : null;

    const handleRateChange = (type, index, field, value) => {
        const setter = type === 'city' ? setCityRates : setTravelRates
        setter((current) => current.map((rate, rateIndex) => rateIndex === index
            ? {...rate, [field]: field === 'duration' ? value : (value === '' ? null : Number(value))}
            : rate))
    }

    const addRate = (type) => {
        if (type === 'city') {
            setCityRates((current) => [...current, {duration: '', incall: null, outcall: null}])
        } else {
            setTravelRates((current) => [...current, {duration: '', incall: null, outcall: null}])
        }
    }

    const deleteRate = async (type, index) => {
        const rates = type === 'city' ? cityRates.filter((_, rateIndex) => rateIndex !== index) : travelRates.filter((_, rateIndex) => rateIndex !== index)
        const saved = await persist({[type === 'city' ? 'cityRates' : 'travelRates']: rates})
        if (saved) type === 'city' ? setCityRates(rates) : setTravelRates(rates)
    }

    const deletePhoto = async (index) => {
        const nextPhotos = photos.filter((_, photoIndex) => photoIndex !== index)
        if (await persist({photos: nextPhotos})) setPhotos(nextPhotos)
    }

    const deleteVideo = async (index) => {
        const nextVideos = videos.filter((_, videoIndex) => videoIndex !== index)
        if (await persist({videos: nextVideos})) setVideos(nextVideos)
    }

    const movePhoto = async (fromIndex, toIndex) => {
        if (!model || saving || fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= photos.length || toIndex >= photos.length) return
        const nextPhotos = [...photos]
        const [movedPhoto] = nextPhotos.splice(fromIndex, 1)
        nextPhotos.splice(toIndex, 0, movedPhoto)
        setPhotos(nextPhotos)
        if (!(await persist({photos: nextPhotos}))) setPhotos(photos)
    }

    const handlePhotoDragStart = (event, index) => {
        if (saving) {
            event.preventDefault()
            return
        }
        event.dataTransfer?.setData('text/plain', String(index))
        if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'
        event.currentTarget.classList.add('dragging')
    }

    const handlePhotoDragOver = (event) => {
        if (saving) return
        event.preventDefault()
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    }

    const handlePhotoDrop = (event, toIndex) => {
        event.preventDefault()
        event.currentTarget.classList.remove('dragging')
        const fromIndex = Number(event.dataTransfer?.getData('text/plain'))
        if (Number.isInteger(fromIndex)) movePhoto(fromIndex, toIndex)
    }

    const handlePhotoDragEnd = (event) => event.currentTarget.classList.remove('dragging')

    const handlePhotoPointerDown = (event, index) => {
        if (saving || event.pointerType === 'mouse' || !event.target.closest?.('.photo-drag-handle')) return
        photoPointerRef.current = {
            element: event.currentTarget,
            fromIndex: index,
            toIndex: index,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            dragging: false,
            targetElement: null
        }
        event.currentTarget.setPointerCapture?.(event.pointerId)
    }

    const handlePhotoPointerMove = (event) => {
        const pointer = photoPointerRef.current
        if (!pointer || pointer.pointerId !== event.pointerId) return
        const distance = Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY)
        if (!pointer.dragging && distance < 8) return

        event.preventDefault()
        pointer.dragging = true
        pointer.element.classList.add('dragging')
        const target = document.elementFromPoint?.(event.clientX, event.clientY)?.closest?.('.campaign-photo')
        const targetIndex = Number(target?.dataset.photoIndex)
        const nextTarget = Number.isInteger(targetIndex) && targetIndex >= 0 && targetIndex < photos.length && target !== pointer.element ? target : null
        if (pointer.targetElement !== nextTarget) {
            pointer.targetElement?.classList.remove('drop-target')
            nextTarget?.classList.add('drop-target')
            pointer.targetElement = nextTarget
        }
        if (Number.isInteger(targetIndex) && targetIndex >= 0 && targetIndex < photos.length) pointer.toIndex = targetIndex
    }

    const handlePhotoPointerEnd = (event, cancelled = false) => {
        const pointer = photoPointerRef.current
        if (!pointer || pointer.pointerId !== event.pointerId) return
        pointer.element.releasePointerCapture?.(event.pointerId)
        pointer.element.classList.remove('dragging')
        pointer.targetElement?.classList.remove('drop-target')
        photoPointerRef.current = null
        if (pointer.dragging && !cancelled) movePhoto(pointer.fromIndex, pointer.toIndex)
    }

    const handlePhotoPointerCancel = (event) => handlePhotoPointerEnd(event, true)

    const handlePhotoKeyDown = (event, index) => {
        const offset = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : 0
        if (!offset) return
        event.preventDefault()
        movePhoto(index, index + offset)
    }

    const handlePhotoChange = (event) => {
        const file = event.target.files?.[0] || null
        event.target.value = ''
        if (!file) return
        if (photos.length >= maxPhotos) {
            setError(`You can upload up to ${maxPhotos} photos.`)
            setPhotoFile(null)
            return
        }
        if (!allowedPhotoTypes.has(file.type) || file.size > maxPhotoSize) {
            setError('Photos must be JPEG, PNG, WebP, GIF, or AVIF files smaller than 10 MB.')
            setPhotoFile(null)
            return
        }
        setError('')
        setPhotoFile(file)
    }

    const addPhoto = async () => {
        if (!photoFile || !model || uploadingPhoto) return
        if (photos.length >= maxPhotos) {
            setError(`You can upload up to ${maxPhotos} photos.`)
            return
        }
        setUploadingPhoto(true)
        setError('')
        setSaveMessage('')
        try {
            const uploadedPhotoUrl = await uploadAccountAdvertisementPhoto(model.uuid, photoFile)
            const nextPhotos = [...photos, uploadedPhotoUrl]
            if (await persist({photos: nextPhotos})) {
                setPhotos(nextPhotos)
                setPhotoFile(null)
                setShowUpload(false)
            }
        } catch (uploadError) {
            setError(uploadError.message || 'We could not upload the photo. Please try again.')
        } finally {
            setUploadingPhoto(false)
        }
    }

    const handleVideoChange = (event) => {
        const file = event.target.files?.[0] || null
        event.target.value = ''
        if (!file) return
        if (videos.length >= maxVideos) {
            setError(`You can upload up to ${maxVideos} videos.`)
            setVideoFile(null)
            return
        }
        if (!allowedVideoTypes.has(file.type) || file.size > maxVideoSize) {
            setError('Videos must be MP4, WebM, or MOV files smaller than 100 MB.')
            setVideoFile(null)
            return
        }
        setError('')
        setVideoFile(file)
    }

    const addVideo = async () => {
        if (!videoFile || !model || uploadingVideo) return
        if (videos.length >= maxVideos) {
            setError(`You can upload up to ${maxVideos} videos.`)
            return
        }
        setUploadingVideo(true)
        setError('')
        setSaveMessage('')
        try {
            const uploadedVideoUrl = await uploadAccountAdvertisementVideo(model.uuid, videoFile)
            const nextVideos = [...videos, uploadedVideoUrl]
            if (await persist({videos: nextVideos})) {
                setVideos(nextVideos)
                setVideoFile(null)
                setShowVideoUpload(false)
            }
        } catch (uploadError) {
            setError(uploadError.message || 'We could not upload the video. Please try again.')
        } finally {
            setUploadingVideo(false)
        }
    }

    const handleConfirmDelete = async () => {
        if (!confirmDelete) return
        await confirmDelete.action()
        setConfirmDelete(null)
    }

    const askDelete = (label, action) => setConfirmDelete({label, action});
    const updateTourStop = (index, key, value) => setTourSchedule((current) => current.map((stop, stopIndex) => stopIndex === index ? {...stop, [key]: value} : stop));
    const addTourStop = () => setTourSchedule((current) => [...current, {city: 'Rome', from: '', to: ''}]);
    const removeTourStop = (index) => setTourSchedule((current) => current.filter((_, stopIndex) => stopIndex !== index));
    const cityOptions = singleCity && !italianCities.includes(singleCity) ? [singleCity, ...italianCities] : italianCities;
    const visibleContactKeys = [...new Set([...contactKeys, ...Object.keys(contacts)])];
    const displayPhotos = photos.length ? photos : [DEFAULT_PROFILE_IMAGE];
    const advertisementPath = model ? modelPublicPath({...model, city: singleCity, citySlug: singleCity}) : '/account/ad';

    if (loading) {
        return <section className="section-header account-page campaign-page"><div className="view-container"><Loader className="campaign-loader" /></div></section>
    }

    if (error && !model) {
        return <section className="section-header account-page campaign-page"><div className="view-container"><h1>Advertisement<span>Studio</span></h1><Link to={'/account'} className="back-to-dash">Dashboard →</Link><div className="campaign-feedback campaign-error" role="alert"><p>{error}</p><button type="button" className="section-btn" onClick={() => setRetryToken((value) => value + 1)}>Try again</button></div></div></section>
    }

    return (
        <section className="section-header account-page campaign-page">
            <div className="view-container">
                <h1>Advertisement<span>Studio</span></h1>
                <div className="campaign-account-layout">
                    <nav className="dashboard-nav campaign-nav" aria-label="Member area">
                        <Link to={'/account'} className="dash-nav-item">Dashboard</Link>
                        <Link to={'/account/balance'} className="dash-nav-item">Finances</Link>
                        <Link to={'/account/ad'} className="dash-nav-item active" aria-current="page">Advertisement</Link>
                        <Link to={'/account/store'} className="dash-nav-item">Store</Link>
                    </nav>
                    <div className="dashboard-content campaign-content">
                        {error && <p className="campaign-error-message" role="alert">{error}</p>}
                        <div className="campaign-section campaign-status-section">
                            <div className="section-title-row"><p>Model Status</p></div>
                            <div className="campaign-status-controls">
                                <div className="campaign-status-options" role="group" aria-label="Model status">
                                    {modelStatusOptions.map((option) => <button type="button" key={option.value} className={`campaign-status-option${status === option.value ? ' active' : ''}`} aria-pressed={status === option.value} onClick={() => setStatus(option.value)} disabled={saving}>{option.label}</button>)}
                                </div>
                            </div>
                        </div>
                <div className="campaign-layout">
                    <div className="campaign-left">
                        <div className="campaign-section">
                            <div className="section-title-row"><p>Name</p></div>
                            <input className="campaign-name-input" value={name} onChange={(event) => setName(event.target.value)} />
                        </div>

                        <div className="campaign-section">
                            <div className="section-title-row"><p>Photos</p><button type="button" className="section-btn campaign-media-add-button" onClick={() => setShowUpload(true)} disabled={photos.length >= maxPhotos || saving || uploadingPhoto} aria-label="Add photo" title={photos.length >= maxPhotos ? `Maximum ${maxPhotos} photos` : 'Add photo'}><span className="campaign-media-add-label">Add</span></button></div>
                            <div className="campaign-photos" role="list">
                                {displayPhotos.map((photo, index) => <div className={`campaign-photo${photos.length ? ' is-draggable' : ''}`} key={photo} data-photo-index={index} draggable={photos.length > 0 && !saving} tabIndex={photos.length ? 0 : undefined} role={photos.length ? 'listitem' : undefined} aria-label={photos.length ? `Photo ${index + 1} of ${photos.length}. Use arrow keys to reorder.` : undefined} onDragStart={photos.length ? (event) => handlePhotoDragStart(event, index) : undefined} onDragOver={photos.length ? handlePhotoDragOver : undefined} onDrop={photos.length ? (event) => handlePhotoDrop(event, index) : undefined} onDragEnd={photos.length ? handlePhotoDragEnd : undefined} onPointerDown={photos.length ? (event) => handlePhotoPointerDown(event, index) : undefined} onPointerMove={photos.length ? handlePhotoPointerMove : undefined} onPointerUp={photos.length ? handlePhotoPointerEnd : undefined} onPointerCancel={photos.length ? handlePhotoPointerCancel : undefined} onKeyDown={photos.length ? (event) => handlePhotoKeyDown(event, index) : undefined}>
                                    {photos.length > 0 && <span className="photo-drag-handle" aria-hidden="true">Drag</span>}
                                    <img src={photo} alt={photos.length ? `${name || 'Model'} ${index + 1}` : `${name || 'Model'} profile placeholder`} onClick={() => photos.length && setLightbox(photo)} />
                                    {photos.length > 0 && <button type="button" className="photo-delete" onClick={() => askDelete('this photo', () => deletePhoto(index))} aria-label={`Delete photo ${index + 1}`}>&times;</button>}
                                </div>)}
                            </div>
                        </div>

                        <div className="campaign-section campaign-video-section">
                            <div className="section-title-row"><p>Video</p><button type="button" className="section-btn campaign-media-add-button" onClick={() => setShowVideoUpload(true)} disabled={videos.length >= maxVideos || saving || uploadingVideo} aria-label="Add video" title={videos.length >= maxVideos ? `Maximum ${maxVideos} videos` : 'Add video'}><span className="campaign-media-add-label">Add</span></button></div>
                            <div className="campaign-videos" role="list">
                                {videos.length ? videos.map((video, index) => <div className="campaign-video" key={video} role="listitem">
                                    <video controls preload="metadata" src={video} aria-label={`Advertisement video ${index + 1}`} />
                                    <button type="button" className="photo-delete video-delete" onClick={() => askDelete('this video', () => deleteVideo(index))} aria-label={`Delete video ${index + 1}`}>&times;</button>
                                </div>) : <p className="campaign-media-empty">No videos added yet.</p>}
                            </div>
                        </div>
                    </div>
                    <div className="campaign-right">
                        <div className="campaign-section">
                            <div className="section-title-row"><p>Description</p></div>
                            <textarea className="campaign-textarea" rows={8} value={description} onChange={(event) => setDescription(event.target.value)} />
                        </div>

                        <div className="campaign-section">
                            <div className="section-title-row"><p>Contacts</p></div>
                            <div className="campaign-contacts">{visibleContactKeys.map((key) => <div className="contact-item" key={key}><span className="contact-label">{contactLabel(key)}</span><input className="contact-input" type={key === 'email' ? 'email' : 'text'} value={contacts[key] || ''} onChange={(event) => setContacts((current) => ({...current, [key]: event.target.value}))} /></div>)}</div>
                        </div>

                        <div className="campaign-section">
                            <div className="section-title-row"><p>Model Parameters</p></div>
                            <div className="campaign-params">{Object.entries(params).map(([key, value]) => <div className="param-item" key={key}><span className="param-label">{key}</span>{paramSelect(key) ? <select className="param-input" value={value} onChange={(event) => handleParamChange(key, event.target.value)}>{paramSelect(key).map((option) => <option key={option}>{option}</option>)}</select> : <input className="param-input" value={value} onChange={(event) => handleParamChange(key, event.target.value)} />}</div>)}</div>
                        </div>

                        <div className="campaign-section">
                            <div className="section-title-row"><p>Location</p></div>
                            <div className="location-toggle"><button type="button" className={locationType === 'single' ? 'loc-btn active' : 'loc-btn'} onClick={() => setLocationType('single')}>Single City</button><button type="button" className={locationType === 'tour' ? 'loc-btn active' : 'loc-btn'} onClick={() => setLocationType('tour')}>Tour Schedule</button></div>
                            {locationType === 'single' ? <div className="single-city"><select value={singleCity} onChange={(event) => setSingleCity(event.target.value)}>{cityOptions.map((city) => <option key={city}>{city}</option>)}</select></div> : <div className="tour-schedule">{tourSchedule.map((stop, index) => <div className="tour-stop" key={`stop-${index}`}><select value={stop.city || 'Rome'} onChange={(event) => updateTourStop(index, 'city', event.target.value)}>{italianCities.map((city) => <option key={city}>{city}</option>)}</select><input type="date" value={stop.from || ''} onChange={(event) => updateTourStop(index, 'from', event.target.value)} /><input type="date" value={stop.to || ''} onChange={(event) => updateTourStop(index, 'to', event.target.value)} /><button type="button" className="table-action delete" onClick={() => askDelete('this tour stop', () => removeTourStop(index))}>×</button></div>)}<button type="button" className="section-btn" onClick={addTourStop}>Add Stop</button></div>}
                        </div>

                        <div className="campaign-section">
                            <div className="section-title-row"><p>City Rates</p><button type="button" className="section-btn" onClick={() => addRate('city')}>Add Rate</button></div>
                            <table className="campaign-table"><thead><tr><th>Duration</th><th>Incall</th><th>Outcall</th><th></th></tr></thead><tbody>{cityRates.map((rate, index) => <tr key={`city-${index}`}><td><select value={rate.duration || ''} onChange={(event) => handleRateChange('city', index, 'duration', event.target.value)}><option value="">Select</option>{durationOptions.map((duration) => <option key={duration}>{duration}</option>)}</select></td><td><input type="number" value={valueOrEmpty(rate.incall)} onChange={(event) => handleRateChange('city', index, 'incall', event.target.value)} /></td><td><input type="number" value={valueOrEmpty(rate.outcall)} onChange={(event) => handleRateChange('city', index, 'outcall', event.target.value)} /></td><td><button type="button" className="table-action delete" onClick={() => askDelete('this city rate', () => deleteRate('city', index))}>×</button></td></tr>)}</tbody></table>
                        </div>

                        <div className="campaign-section">
                            <div className="section-title-row"><p>Travel Rates</p><button type="button" className="section-btn" onClick={() => addRate('travel')}>Add Rate</button></div>
                            <table className="campaign-table"><thead><tr><th>Duration</th><th>Incall</th><th>Outcall</th><th></th></tr></thead><tbody>{travelRates.map((rate, index) => <tr key={`travel-${index}`}><td><select value={rate.duration || ''} onChange={(event) => handleRateChange('travel', index, 'duration', event.target.value)}><option value="">Select</option>{durationOptions.map((duration) => <option key={duration}>{duration}</option>)}</select></td><td><input type="number" value={valueOrEmpty(rate.incall)} onChange={(event) => handleRateChange('travel', index, 'incall', event.target.value)} /></td><td><input type="number" value={valueOrEmpty(rate.outcall)} onChange={(event) => handleRateChange('travel', index, 'outcall', event.target.value)} /></td><td><button type="button" className="table-action delete" onClick={() => askDelete('this travel rate', () => deleteRate('travel', index))}>×</button></td></tr>)}</tbody></table>
                        </div>
                    </div>
                </div>
                {saveMessage && <p className="campaign-success" role="status">{saveMessage}</p>}
                    </div>
                </div>
            </div>
            <div className={dockStopped ? 'campaign-save-dock stopped' : 'campaign-save-dock'}>
                <div className="campaign-save-dock-inner">
                    <Link to={advertisementPath} className="campaign-global-view-button">View Advertisement</Link>
                    <button type="button" className="campaign-global-save-button" onClick={saveAll} disabled={saving}>{saving ? 'Saving…' : 'Save all changes'}</button>
                </div>
            </div>

            {lightbox && <div className="lightbox-overlay" onClick={() => setLightbox(null)}><button type="button" className="lightbox-close" onClick={() => setLightbox(null)}>&times;</button><img src={lightbox} alt={`${name} enlarged`} className="lightbox-img" /></div>}
            {confirmDelete && <div className="deposit-modal-overlay" onClick={() => setConfirmDelete(null)}><div className="deposit-modal" onClick={(event) => event.stopPropagation()}><div className="deposit-modal-header"><h2>Are you sure?</h2><button type="button" className="deposit-modal-close" onClick={() => setConfirmDelete(null)}>&times;</button></div><p className="deposit-desc">Are you sure you want to delete {confirmDelete.label}? This action cannot be undone.</p><div className="confirm-actions"><button type="button" className="deposit-submit" onClick={handleConfirmDelete}>Delete</button><button type="button" className="deposit-back" onClick={() => setConfirmDelete(null)}>Cancel</button></div></div></div>}
            {showUpload && <div className="deposit-modal-overlay" onClick={() => { if (!uploadingPhoto) { setShowUpload(false); setPhotoFile(null) } }}><div className="deposit-modal" onClick={(event) => event.stopPropagation()}><div className="deposit-modal-header"><h2>Upload Photo</h2><button type="button" className="deposit-modal-close" onClick={() => { if (!uploadingPhoto) { setShowUpload(false); setPhotoFile(null) } }}>&times;</button></div><p className="deposit-desc">Select a JPEG, PNG, WebP, GIF, or AVIF image smaller than 10 MB.</p><div className="upload-area"><input id="advertisement-photo-file" className="upload-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/avif" onChange={handlePhotoChange} disabled={uploadingPhoto} /><label htmlFor="advertisement-photo-file" className="upload-label"><span className="upload-icon">+</span><span className="upload-text">{photoFile ? photoFile.name : 'Choose file'}</span></label><button type="button" className="deposit-submit" onClick={addPhoto} disabled={!photoFile || uploadingPhoto}>{uploadingPhoto ? 'Uploading…' : 'Upload Photo'}</button></div></div></div>}
            {showVideoUpload && <div className="deposit-modal-overlay" onClick={() => { if (!uploadingVideo) { setShowVideoUpload(false); setVideoFile(null) } }}><div className="deposit-modal" onClick={(event) => event.stopPropagation()}><div className="deposit-modal-header"><h2>Upload Video</h2><button type="button" className="deposit-modal-close" onClick={() => { if (!uploadingVideo) { setShowVideoUpload(false); setVideoFile(null) } }}>&times;</button></div><p className="deposit-desc">Select an MP4, WebM, or MOV video smaller than 100 MB.</p><div className="upload-area"><input id="advertisement-video-file" className="upload-input" type="file" accept="video/mp4,video/webm,video/quicktime" onChange={handleVideoChange} disabled={uploadingVideo} /><label htmlFor="advertisement-video-file" className="upload-label"><span className="upload-icon">+</span><span className="upload-text">{videoFile ? videoFile.name : 'Choose file'}</span></label><button type="button" className="deposit-submit" onClick={addVideo} disabled={!videoFile || uploadingVideo}>{uploadingVideo ? 'Uploading…' : 'Upload Video'}</button></div></div></div>}
        </section>
    )
}

export default Advertisement
